"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
    m,
    useScroll,
    useTransform,
    LazyMotion,
    domAnimation,
    MotionValue,
} from "framer-motion";
import Image from "next/image";
import CtaButton from "@/components/ui/CtaButton";

// --- TYPY ---
type ImageProps = {
    src: string;
    alt?: string;
};

type ContentProps = {
    heading: string;
    description: string;
};

type Props = {
    copy: ContentProps;
    images: ImageProps[];
};

export type Layout425Props = Omit<
    React.ComponentPropsWithoutRef<"section">,
    "content"
> &
    Partial<Props>;

// --- CONFIG ---
const Layout425Defaults: Props = {
    copy: {
        heading: "Zaplanuj swój event z organizatorem imprez z Wrocławia",
        description:
            "Napisz do nas, opowiedz o wydarzeniu, a my przygotujemy ofertę dopasowaną do Twoich celów i budżetu. Bez zobowiązań, bez ukrytych kosztów.",
    },
    images: [
        { src: "/cta_4.webp", alt: "1" },
        { src: "/cta_2.webp", alt: "2" },
        { src: "/cta_5.webp", alt: "3" },
        { src: "/cta_1.webp", alt: "4" },
        { src: "/cta_3.webp", alt: "5" },
    ],
};

const IMG_CLASSES = {
    one:  "absolute -left-16 bottom-[80%] w-full max-w-[220px] lg:max-w-[300px] md:bottom-3/4 md:left-[20%]",
    two:  "absolute -left-8 top-[72%] w-full max-w-[220px] lg:max-w-[300px] md:left-auto md:top-[65%]",
    three:"absolute top-[75%] w-full max-w-[220px] lg:max-w-[380px] md:-right-[80%] md:top-[80%]",
    four: "absolute -right-8 bottom-[78%] w-full max-w-[220px] lg:max-w-[380px] md:bottom-[70%] md:right-0",
    five: "absolute -right-16 bottom-[15%] flex w-full max-w-[220px] lg:max-w-[300px] items-center md:-bottom-[5%] md:right-[10%] lg:bottom-[-15%]",
};

// --- LOGIKA ANIMACJI (DESKTOP ONLY) ---
const useDesktopTransforms = (scrollYProgress: MotionValue<number>) => {
    return {
        content: {
            opacity: useTransform(scrollYProgress, [0, 1], [0, 1]),
            scale: useTransform(scrollYProgress, [0, 1], [0.95, 1]),
            y: useTransform(scrollYProgress, [0, 1], ["-2vh", "4vh"]),
        },
        imageOne: {
            x: useTransform(scrollYProgress, [0, 1], ["90%", "0%"]),
            y: useTransform(scrollYProgress, [0, 1], ["70%", "0%"]),
        },
        imageTwo: {
            x: useTransform(scrollYProgress, [0, 1], ["90%", "0%"]),
            y: useTransform(scrollYProgress, [0, 1], ["-50%", "0%"]),
        },
        imageThree: {
            x: useTransform(scrollYProgress, [0, 1], ["10%", "0%"]),
            y: useTransform(scrollYProgress, [0, 1], ["-130%", "0%"]),
        },
        imageFour: {
            x: useTransform(scrollYProgress, [0, 1], ["-80%", "0%"]),
            y: useTransform(scrollYProgress, [0, 1], ["60%", "0%"]),
        },
        imageFive: {
            x: useTransform(scrollYProgress, [0, 1], ["-60%", "0%"]),
            y: useTransform(scrollYProgress, [0, 1], ["-60%", "0%"]),
        },
    };
};

// ✅ Prosty hook: animacje tylko na LG+
function useIsDesktopLg(): boolean {
    const [isDesktop, setIsDesktop] = useState(false);

    useEffect(() => {
        const mq = window.matchMedia("(min-width: 1024px)");
        const update = () => setIsDesktop(mq.matches);
        update();
        mq.addEventListener("change", update);
        return () => mq.removeEventListener("change", update);
    }, []);

    return isDesktop;
}

export const CTASection = (props: Layout425Props) => {
    const { copy, images } = { ...Layout425Defaults, ...props };
    const sectionRef = useRef<HTMLElement>(null);

    const isDesktopLg = useIsDesktopLg();

    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ["start start", "end end"],
    });

    const transforms = useDesktopTransforms(scrollYProgress);

    // ✅ NA MOBILE: zero animacji (zawsze widoczne, brak “reverse”)
    const contentStyle = useMemo(() => {
        if (!isDesktopLg) return undefined;
        return transforms.content;
    }, [isDesktopLg, transforms.content]);

    return (
        <LazyMotion features={domAnimation}>
            <section
                id="relume"
                ref={sectionRef}
                className="relative pt-16 pb-2 lg:h-[200vh] lg:py-0 lg:pb-[8%]"
            >
                <div className="relative h-full">
                    <div className="static lg:sticky lg:top-0 lg:z-10 lg:flex lg:min-h-screen lg:flex-col lg:items-center lg:justify-center lg:overflow-visible">
                        {/* --- IMAGES WRAPPER (HIDDEN ON MOBILE/TABLET) --- */}
                        <div className="hidden lg:block pointer-events-none">
                            <div className="absolute bottom-0 left-0 right-0 top-0 z-20 flex h-full w-full items-center justify-center md:right-auto md:ml-[5%] md:w-[30%] md:justify-start">
                                <m.div className={IMG_CLASSES.one} style={transforms.imageOne}>
                                    <OptimizedImage src={images[0].src} alt={images[0].alt} />
                                </m.div>
                                <m.div className={IMG_CLASSES.two} style={transforms.imageTwo}>
                                    <OptimizedImage src={images[1].src} alt={images[1].alt} />
                                </m.div>
                                <m.div
                                    className={IMG_CLASSES.three}
                                    style={transforms.imageThree}
                                >
                                    <OptimizedImage src={images[2].src} alt={images[2].alt} />
                                </m.div>
                            </div>

                            <div className="absolute bottom-0 right-0 top-0 z-20 flex h-full w-full items-center justify-center md:mx-[5%] md:w-[30%] md:justify-end">
                                <m.div className={IMG_CLASSES.four} style={transforms.imageFour}>
                                    <OptimizedImage src={images[3].src} alt={images[3].alt} />
                                </m.div>
                                <m.div className={IMG_CLASSES.five} style={transforms.imageFive}>
                                    <OptimizedImage src={images[4].src} alt={images[4].alt} />
                                </m.div>
                            </div>
                        </div>

                        {/* --- CONTENT (VISIBLE ALWAYS) --- */}
                        <m.div className="relative z-10 px-[5%]" style={contentStyle}>
                            <div className="container max-w-lg">
                                <div className="text-center">
                                    <h2 className="rb-5 mb-5 text-4xl font-medium md:mb-6 md:text-7xl">
                                        {copy.heading}
                                    </h2>
                                    <p className="md:text-md max-w-md mx-auto">{copy.description}</p>
                                    <div className="mt-6 flex flex-wrap items-center justify-center gap-4 md:mt-8">
                                        <CtaButton />
                                    </div>
                                </div>
                            </div>
                        </m.div>
                    </div>
                </div>
            </section>
        </LazyMotion>
    );
};

function OptimizedImage({ src, alt }: { src: string; alt?: string }) {
    return (
        <div className="relative w-full h-full rounded-2xl overflow-hidden shadow-lg">
            <Image
                src={src}
                alt={alt || "CTA image"}
                width={400}
                height={300}
                sizes="(max-width: 768px) 100vw, 33vw"
                className="object-cover"
            />
        </div>
    );
}